import React, { useState, useCallback, useEffect, useRef } from 'react';
import { showAlert, showConfirm } from '../common/AppDialog';
import classNames from 'classnames';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { setCurrentWorkflow, setSelectedNode, centerOnNode, removeNode } from '../../store/workflow/workflowSlice';
import { Project, Workflow, createWorkflow, retireWorkflow, saveWorkflowMeta, loadWorkflows } from '../../store/project/projectSlice';
import { loadCanvas, saveCanvas } from '../../services/repo';
import { openWorkflow } from '../../store/workflow/openWorkflow';
import { assertWritable } from '../../services/canvasSchema';
import { isCanvasDirty } from '../../utils/canvasSnapshot';
import { logError } from '../../services/eventLog';
import { FiChevronDown, FiChevronRight, FiPlus, FiSettings, FiTrash2, FiBox, FiCircle, FiZap, FiCopy, FiFolder, FiFolderPlus, FiEdit2 } from 'react-icons/fi';
import {
  buildFolderTree, listFolderPaths, normalizeFolder, joinFolder, parentFolder, replacePrefix,
  validateFolderName, loadEmptyFolders, saveEmptyFolders, isInside, FolderNode,
} from '../../utils/workflowFolders';
import { showPrompt } from '../common/AppDialog';
import WorkflowWizard from '../workflow/wizard/WorkflowWizard';
import { IconBaseProps } from 'react-icons/lib';
import IconWrapper from '../common/IconWrapper';
import WorkflowLoadNotice from '../common/WorkflowLoadNotice';
import { useTranslation } from 'react-i18next';
import ProjectSettingsDialog from './ProjectSettingsDialog';
import WorkflowSettingsDialog from '../workflow/WorkflowSettingsDialog';

interface WorkflowExplorerProps {
}

const WorkflowExplorer: React.FC<WorkflowExplorerProps> = () => {
  const { t } = useTranslation();
  const projects = useSelector((state: RootState) => state.project.projects);
  const workflows = useSelector((state: RootState) => state.project.workflows);
  const nodes = useSelector((state: RootState) => state.workflow.nodes);
  const currentWorkflow = useSelector((state: RootState) => state.workflow.current);
  const edges = useSelector((state: RootState) => state.workflow.edges);
  const savedSnapshots = useSelector((state: RootState) => state.project.savedSnapshots);
  const settings = useSelector((state: RootState) => state.workflow.settings);
  const dupUsrid = useSelector((state: RootState) => state.auth.user?.usrid || '');
  /** 미저장 변경 여부 — 열려 있는 워크플로는 캔버스 상태로, 나머지는 메모리 캐시로 판정 */
  const isDirty = (w: Workflow) => (currentWorkflow?.id === w.id ? isCanvasDirty(savedSnapshots[w.id], nodes, edges, settings) : (w.nodes !== undefined && isCanvasDirty(savedSnapshots[w.id], w.nodes, w.edges, w.settings)));
  // 저장하지 않은 변경이 있으면 탭을 닫거나 새로고침하기 전에 브라우저가 한 번 묻는다 — 편집은 메모리에만 있다.
  // 실행 중에는 저장할 수 없으므로(2026-10-12) 그동안 고친 것을 실수로 잃지 않게 하는 장치이기도 하다.
  // (문구는 브라우저가 정한다 — 페이지가 넣은 글은 보이지 않는다)
  const anyDirty = workflows.some(isDirty);
  useEffect(() => {
    if (!anyDirty) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [anyDirty]);
  const selectedNodeId = useSelector((state: RootState) => state.workflow.selectedNodeId);
  const selectedProjectId = useSelector((state: RootState) => state.project.selectedProjectId);
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();

  // 펼침/접힘 상태 관리
  const [expandedProjects, setExpandedProjects] = useState<Record<string, boolean>>({});
  /** 프로젝트 설정 창 (시간대) */
  const [projectSettingsOpen, setProjectSettingsOpen] = useState(false);
  const [expandedWorkflows, setExpandedWorkflows] = useState<Record<string, boolean>>({});

  // 워크플로 설정 모달 상태
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [currentEditingWorkflow, setCurrentEditingWorkflow] = useState<Workflow | null>(null);
  const [isNewWorkflow, setIsNewWorkflow] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);   // 워크플로 작성 마법사
  const [wizardFolder, setWizardFolder] = useState('');  // 마법사가 만들 워크플로를 넣을 폴더
  // 폴더 펼침 상태 (기본 펼침 — 접은 것만 기억한다)
  const [collapsedFolders, setCollapsedFolders] = useState<Record<string, boolean>>({});
  // 아직 워크플로가 없는 폴더 — 이 브라우저에만 기억한다 (워크플로를 넣으면 서버에 남는다)
  const [emptyFolders, setEmptyFolders] = useState<string[]>([]);
  useEffect(() => { setEmptyFolders(selectedProjectId ? loadEmptyFolders(selectedProjectId) : []); }, [selectedProjectId]);
  const rememberEmpty = (next: string[]) => {
    setEmptyFolders(next);
    if (selectedProjectId) saveEmptyFolders(selectedProjectId, next);
  };

  // 선택된 프로젝트 자동 펼침
  useEffect(() => {
    if (selectedProjectId) {
      setExpandedProjects(prev => ({ ...prev, [selectedProjectId]: true }));
    }
  }, [selectedProjectId]);

  const toggleProject = (projectId: string) => {
    setExpandedProjects((prev) => ({ ...prev, [projectId]: !prev[projectId] }));
  };

  const toggleWorkflow = (workflowId: string) => {
    setExpandedWorkflows((prev) => ({ ...prev, [workflowId]: !prev[workflowId] }));
  };

  /** 캔버스에 노드/엣지를 흘려 넣는다 (workflowSlice 는 addNode/addEdge 로만 채운다) */
  /** 워크플로 복제 — 메타 + 캔버스(편집 중이면 리덕스 캐시, 아니면 저장된 현재 버전)를 사본으로 */
  const handleDuplicateWorkflow = async (wf: Workflow, e: React.MouseEvent) => {
    e.stopPropagation();
    // 원본이 더 새 형식이면 사본을 만들지 않는다 — 이 빌드가 모르는 필드를 떨어뜨린 사본이 생기고,
    // 원본과 달라진 것을 알아챌 방법이 없다. 원본 자체는 그대로다.
    try { assertWritable(wf.schemaInfo, 'duplicate'); }
    catch (err) { showAlert((err as Error).message, { title: t('layout:explorer.tooNewTitle'), tone: 'warning' }); return; }
    const r = await dispatch(createWorkflow({ projectId: wf.projectId, name: t('layout:explorer.copyName', { name: wf.name }), description: wf.description, icon: wf.icon }));
    if (createWorkflow.rejected.match(r)) { showAlert(t('layout:explorer.duplicateFailed', { message: r.error?.message || '' })); return; }
    const copy = r.payload as Workflow;
    try {
      const doc = wf.nodes !== undefined
        ? { nodes: wf.nodes ?? [], edges: wf.edges ?? [], settings: (wf as any).settings }
        : await loadCanvas(wf.id, wf.curVerNo ?? 0);
      if (doc && ((doc.nodes?.length ?? 0) > 0 || (doc.edges?.length ?? 0) > 0)) {
        await saveCanvas(copy.id, 0, { nodes: doc.nodes ?? [], edges: doc.edges ?? [], settings: doc.settings }, dupUsrid);
      }
    } catch (err) {
      showAlert(t('layout:explorer.copyCanvasFailed', { message: (err as Error).message }));
    }
    dispatch(loadWorkflows(wf.projectId));
  };

  /** 워크플로 열기 — 어시스턴트 도구(open_sample_workflow 등)와 같은 경로(store/workflow/openWorkflow) */
  const handleWorkflowClick = async (workflow: Workflow) => {
    try {
      await openWorkflow(store, workflow);
    } catch (err) {
      showAlert(t('layout:explorer.loadCanvasFailed', { message: (err as Error).message || '' }));
    }
  };

  /** 탐색기에서 노드 삭제 — 캔버스의 삭제 아이콘과 같은 일(연결도 함께 지운다) */
  const handleRemoveNode = async (node: any, e: React.MouseEvent) => {
    e.stopPropagation();
    const name = getNodeDisplayName(node);
    if (!(await showConfirm(t('layout:explorer.deleteNodeConfirm', { name })))) return;
    dispatch(removeNode(node.id));
  };

  const handleNodeClick = (nodeId: string) => {
    dispatch(setSelectedNode(nodeId));
    dispatch(centerOnNode(nodeId));
  };

  const handleAddWorkflow = (projectId: string, folder = '') => {
    // ID 는 저장 시점에 QBase 채번(오픈플로우워크플로ID) — 여기서는 모달용 임시 껍데기만
    const newWorkflow: Workflow = {
      id: '',
      projectId,
      name: t('layout:explorer.newWorkflowName'),
      icon: '📄',
      folder: normalizeFolder(folder) || undefined,
    };

    setCurrentEditingWorkflow(newWorkflow);
    setIsNewWorkflow(true);
    setSettingsModalOpen(true);
  };

  const handleEditWorkflow = async (workflow: Workflow, e: React.MouseEvent) => {
    e.stopPropagation();
    // 스케줄·재시도 같은 실행 설정은 그 워크플로의 캔버스 문서에 있다 — 열려 있지 않으면 먼저 연다
    if (currentWorkflow?.id !== workflow.id) {
      try { await openWorkflow(store, workflow); }
      catch (err) { showAlert(t('layout:explorer.loadCanvasFailed', { message: (err as Error).message || '' })); return; }
    }
    setCurrentEditingWorkflow(workflow);
    setIsNewWorkflow(false);
    setSettingsModalOpen(true);
  };

  const handleRemoveWorkflow = async (workflowId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (await showConfirm(t('layout:explorer.deleteConfirm'), { title: t('layout:explorer.deleteTitle'), okText: t('layout:explorer.deleteOk'), tone: 'danger' })) {
      dispatch(retireWorkflow(workflowId));   // 소프트 삭제 (USE_YN='N')
    }
  };

  const handleSaveWorkflow = async (data: { id: string; name: string; description?: string; folder: string }) => {
    const prev = currentEditingWorkflow;
    // 실행 설정만 바꿨으면 서버에 이름·폴더를 다시 쓰지 않는다 (실행 설정은 창이 캔버스에 넣었다 — Save 로 저장)
    const metaSame = !isNewWorkflow && !!prev && prev.name === data.name
      && (prev.description || undefined) === data.description && normalizeFolder(prev.folder || '') === data.folder;
    const r = metaSame ? {}
      : (isNewWorkflow && prev)
        ? await dispatch(createWorkflow({ projectId: prev.projectId, name: data.name, description: data.description, icon: '📄', folder: data.folder }))
        : await dispatch(saveWorkflowMeta(data));
    if ((r as any).error) {
      showAlert(t('layout:explorer.saveFailed', { message: (r as any).error?.message || '' }));
      return;
    }
    // 워크플로가 들어간 폴더(와 그 상위)는 이제 서버가 안다 — 브라우저 기억에서 뺀다
    if (data.folder) rememberEmpty(emptyFolders.filter((f) => !isInside(data.folder, f)));
    // 새 워크플로를 만들면 그 폴더에서 펼쳐 둔 워크플로(노드 목록)를 접는다 — 새 줄이 긴 노드 목록 아래에 묻히지 않게
    if (isNewWorkflow) {
      const here = normalizeFolder(data.folder || '');
      const sameFolder = projectWorkflows.filter((w: Workflow) => normalizeFolder(w.folder || '') === here).map((w: Workflow) => w.id);
      setExpandedWorkflows((prev) => {
        const next = { ...prev };
        sameFolder.forEach((id: string) => { next[id] = false; });
        return next;
      });
      setCollapsedFolders((c) => ({ ...c, [here]: false }));
    }
    setSettingsModalOpen(false);
    setCurrentEditingWorkflow(null);
  };

  // ---- 폴더 ----
  const handleAddFolder = async (parent: string) => {
    const name = await showPrompt(parent ? t('layout:explorer.newFolderUnder', { parent }) : t('layout:explorer.newFolderName'), { title: t('layout:explorer.addFolderTitle'), okText: t('layout:explorer.create') });
    if (name == null) return;
    const err = validateFolderName(name);
    if (err) { showAlert(err); return; }
    const path = joinFolder(parent, name);
    if (folderPaths.includes(path)) { showAlert(t('layout:explorer.folderExists')); return; }
    rememberEmpty([...emptyFolders, path]);
    setCollapsedFolders((c) => ({ ...c, [parent]: false }));
  };

  /** 폴더 이름 변경 — 그 아래 모든 워크플로의 경로를 고쳐 쓴다 (서버 왕복 n건) */
  const handleRenameFolder = async (node: FolderNode) => {
    const name = await showPrompt(t('layout:explorer.folderName'), { title: t('layout:explorer.renameFolderTitle'), okText: t('layout:explorer.rename'), defaultValue: node.name });
    if (name == null || name.trim() === node.name) return;
    const err = validateFolderName(name);
    if (err) { showAlert(err); return; }
    const to = joinFolder(parentFolder(node.path), name);
    if (folderPaths.includes(to)) { showAlert(t('layout:explorer.folderExists')); return; }
    const affected = projectWorkflows.filter((w: Workflow) => isInside(w.folder, node.path));
    for (const w of affected) {
      const r = await dispatch(saveWorkflowMeta({ id: w.id, name: w.name, description: w.description, folder: replacePrefix(w.folder, node.path, to) }));
      if ((r as any).error) { showAlert(t('layout:explorer.renameFailed', { message: (r as any).error?.message || '' })); return; }
    }
    rememberEmpty(emptyFolders.map((f) => replacePrefix(f, node.path, to)));
  };

  /** 폴더 삭제 — 비어 있으면 그냥, 아니면 안의 워크플로를 상위 폴더로 올린 뒤 지운다 */
  const handleDeleteFolder = async (node: FolderNode) => {
    const parent = parentFolder(node.path);
    if (node.total > 0) {
      const ok = await showConfirm(
        t('layout:explorer.deleteFolderBody', { name: node.name, count: node.total, dest: parent ? `'${parent}'` : t('layout:explorer.projectRoot') }),
        { title: t('layout:explorer.deleteFolderTitle'), okText: t('layout:explorer.moveAndDelete'), tone: 'danger' },
      );
      if (!ok) return;
      const affected = projectWorkflows.filter((w: Workflow) => isInside(w.folder, node.path));
      for (const w of affected) {
        const r = await dispatch(saveWorkflowMeta({ id: w.id, name: w.name, description: w.description, folder: replacePrefix(w.folder, node.path, parent) }));
        if ((r as any).error) { showAlert(t('layout:explorer.deleteFolderFailed', { message: (r as any).error?.message || '' })); return; }
      }
    }
    rememberEmpty(emptyFolders.filter((f) => !isInside(f, node.path)));
  };

  const toggleFolder = (path: string) => setCollapsedFolders((c) => ({ ...c, [path]: !c[path] }));

  // ---- 드래그로 다른 폴더로 옮기기 — 워크플로 줄을 끌어 폴더 줄(그 폴더) · 프로젝트 줄(최상위) · 다른 워크플로 줄(그 워크플로의 폴더)에 놓는다
  const DRAG_TYPE = 'application/x-openflow-workflow';
  const draggingRef = useRef<string | null>(null);
  const [dropFolder, setDropFolder] = useState<string | null>(null);   // 놓일 폴더('' = 최상위) — 강조 표시
  const dragProps = (wf: Workflow) => readOnly ? {} : {
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      draggingRef.current = wf.id;
      e.dataTransfer.setData(DRAG_TYPE, wf.id);
      e.dataTransfer.effectAllowed = 'move';
    },
    onDragEnd: () => { draggingRef.current = null; setDropFolder(null); },
  };
  const dropProps = (folder: string) => readOnly ? {} : {
    onDragOver: (e: React.DragEvent) => {
      if (!draggingRef.current) return;                 // 워크플로가 아닌 것(툴박스 등)은 받지 않는다
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      if (dropFolder !== folder) setDropFolder(folder);
    },
    onDragLeave: (e: React.DragEvent) => {
      if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as globalThis.Node)) setDropFolder((f) => (f === folder ? null : f));
    },
    onDrop: (e: React.DragEvent) => {
      const id = draggingRef.current || e.dataTransfer.getData(DRAG_TYPE);
      draggingRef.current = null;
      setDropFolder(null);
      if (!id) return;
      e.preventDefault();
      e.stopPropagation();
      void moveWorkflow(id, folder);
    },
  };
  const moveWorkflow = async (id: string, folder: string) => {
    const w = projectWorkflows.find((x: Workflow) => x.id === id);
    const to = normalizeFolder(folder);
    if (!w || normalizeFolder(w.folder || '') === to) return;
    const r = await dispatch(saveWorkflowMeta({ id: w.id, name: w.name, description: w.description, folder: to }));
    if ((r as any).error) { showAlert(t('layout:explorer.moveFailed', { message: (r as any).error?.message || '' })); return; }
    if (to) rememberEmpty(emptyFolders.filter((f) => !isInside(to, f)));
    setCollapsedFolders((c) => ({ ...c, [to]: false }));
  };
  const dropHighlight = (folder: string) => (dropFolder === folder ? 'ring-2 ring-blue-400 bg-blue-50' : '');

  // 선택된 프로젝트 찾기
  const selectedProject = projects.find(p => p.id === selectedProjectId);
  // 조회자에게는 만들기·복제·삭제·폴더 버튼을 숨긴다 — 눌러도 서버가 막는다(docs/PROJECT_ACCESS.md)
  const readOnly = selectedProject?.myRole === 'viewer';

  // 현재 워크플로 노드 정보를 가져오기 위해 워크플로 노드 상태 추가
  const workflowNodes = useSelector((state: RootState) => state.workflow.nodes);
  const currentWorkflowId = useSelector((state: RootState) => state.workflow.current?.id);

  // 노드 타입에 따른 아이콘과 색상 매핑
  const getNodeIcon = (type: string) => {
    switch(type) {
      case 'rdbData':
        return { icon: FiBox, color: 'text-blue-500' };
      case 'csvData':
        return { icon: FiBox, color: 'text-green-500' };
      case 'placeholder':
        return { icon: FiCircle, color: 'text-gray-400' };
      case 'workflow':
        return { icon: FiCircle, color: 'text-purple-500' };
      default:
        return { icon: FiCircle, color: 'text-gray-500' };
    }
  };

  // 노드 표시 이름 생성
  const getNodeDisplayName = (node: any) => {
    if (node.data?.label) {
      return node.data.label;
    }
    return `${node.type || 'Node'} (${node.id})`;
  };

  const projectState = useSelector((state: any) => state.project);
  const { workflows: projectWorkflows } = projectState;
  // 폴더 트리 — 워크플로의 folder 경로에서 유추 + 아직 빈 폴더(브라우저 기억)
  const tree: FolderNode | null = selectedProjectId
    ? buildFolderTree(projectWorkflows.filter((w: Workflow) => w.projectId === selectedProjectId), emptyFolders)
    : null;
  const folderPaths = tree ? listFolderPaths(tree) : [];

  /** 자동 열기가 겹치지 않게 — 효과는 목록·현재 id 가 바뀔 때마다 다시 돈다 */
  const autoOpeningRef = useRef(false);
  useEffect(() => {
    // 목록에는 다른 프로젝트의 워크플로(캐시)도 남아 있다 — **이 프로젝트 것만** 본다. 예전엔 전부를 봐서,
    // 화면을 그대로 둔 채 프로젝트가 바뀌면(프로젝트 불러오기) 앞 프로젝트의 워크플로가 계속 열려 있었다.
    const mine = projectWorkflows.filter((w: Workflow) => w.projectId === selectedProjectId);
    if (!selectedProjectId || mine.length === 0) return;
    // 이 프로젝트의 워크플로가 이미 현재 워크플로로 잡혀 있으면 그대로 둔다.
    // (예전엔 currentWorkflowId 가 바뀔 때마다 '최종/첫 번째' 로 되돌려서, 탐색기에서 다른 워크플로를
    //  열어도 헤더 배지·Deploy 대상이 첫 번째 워크플로로 스냅되는 버그가 있었다)
    if (mine.some((w: any) => w.id === currentWorkflowId)) return;
    let workflowIdToShow = selectedProject?.finalWorkflowId;
    if (!workflowIdToShow || !mine.some((w: any) => w.id === workflowIdToShow)) {
      workflowIdToShow = mine[0].id;
    }
    if (currentWorkflowId !== workflowIdToShow) {
      const workflowToShow = mine.find((w: any) => w.id === workflowIdToShow);
      // **캔버스까지 읽는다.** 예전에는 '현재 워크플로' 로 표시만 해서, 헤더·탐색기는 열린 것처럼
      // 보이는데 캔버스와 노드 목록은 비어 있었다. 그 상태로 저장하면 내용이 덮인다.
      if (workflowToShow && !autoOpeningRef.current) {
        autoOpeningRef.current = true;
        openWorkflow(store, workflowToShow)
          .catch((err) => logError('workflow.open', err as Error, { target: workflowToShow.id, detail: { auto: true } }))
          .finally(() => { autoOpeningRef.current = false; });
      }
    }
  }, [selectedProjectId, selectedProject?.finalWorkflowId, projectWorkflows, currentWorkflowId, dispatch]);

  /** 워크플로 한 줄 (노드 목록 포함) — 폴더 트리가 재귀로 부른다 */
  const renderWorkflow = (workflow: Workflow) => {
                        const isWorkflowExpanded = expandedWorkflows[workflow.id];
                        const isCurrentWorkflow = currentWorkflowId === workflow.id;

                        // 현재 워크플로에 속한 노드들 필터링
                        const workflowNodesList = isCurrentWorkflow ? workflowNodes : [];

                        return (
                          <div key={workflow.id}>
                            <div
                              {...dragProps(workflow)}
                              {...dropProps(workflow.folder || '')}
                              title={readOnly ? undefined : t('layout:explorer.dragToMove')}
                              className={classNames(
                                'relative flex items-center group cursor-pointer px-2 py-1 rounded workflow-explorer-item',
                                isCurrentWorkflow ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-100 text-gray-700'
                              )}
                              onClick={() => {
                                handleWorkflowClick(workflow);
                                // 워크플로 클릭 시 자동으로 펼치기
                                if (!isWorkflowExpanded) {
                                  toggleWorkflow(workflow.id);
                                }
                              }}
                            >
                              {/* 화살표는 회전으로 부드럽게 — 아이콘 교체 대신 하나를 돌린다 */}
                              <span className={`tree-chevron ${isWorkflowExpanded ? 'open' : ''}`}>
                                <IconWrapper
                                  icon={FiChevronRight}
                                  size={14}
                                  onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                                    e.stopPropagation();
                                    toggleWorkflow(workflow.id);
                                  }}
                                />
                              </span>
                              <span className="ml-1 flex-1">{workflow.name}</span>
                              {isDirty(workflow) && (
                                <span className="wf-dirty-dot" title={t('layout:explorer.unsaved')}>●</span>
                              )}
                              {/* 동작 아이콘은 자리를 미리 잡지 않는다 — 이름이 눌려 안 보였다(2026-10-12). 마우스를 올리면 이름 위에 겹쳐 뜬다 */}
                              <span className="absolute right-1 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center bg-white border border-gray-200 rounded shadow-sm z-10 gap-0.5">
                                <button
                                  onClick={(e) => handleEditWorkflow(workflow, e)}
                                  className="p-1 rounded hover:bg-gray-200"
                                  title={t('layout:explorer.settingsTitle')}
                                >
                                  <IconWrapper icon={FiSettings} size={12} />
                                </button>
                                {!readOnly && (<>
                                <button
                                  onClick={(e) => handleDuplicateWorkflow(workflow, e)}
                                  className="p-1 rounded hover:bg-gray-200"
                                  title={t('layout:explorer.duplicateTitle')}
                                >
                                  <IconWrapper icon={FiCopy} size={12} />
                                </button>
                                <button
                                  onClick={(e) => handleRemoveWorkflow(workflow.id, e)}
                                  className="p-1 rounded hover:bg-red-100"
                                  title={t('layout:explorer.deleteTitle')}
                                >
                                  <IconWrapper icon={FiTrash2} size={12} />
                                </button>
                                </>)}
                              </span>
                            </div>
                            {/* 접힘/펼침을 부드럽게 — 높이를 모르는 채로 애니메이션하려고 grid-template-rows 0fr↔1fr 을 쓴다.
                                (조건부 언마운트로는 접히는 동작이 애니메이션되지 않는다) */}
                            {isCurrentWorkflow && (
                              <div className={`tree-collapse ${isWorkflowExpanded ? 'open' : ''}`}>
                                <div>
                              <div className="ml-5 border-l border-gray-100 pl-2">
                                {workflowNodesList.length === 0 ? (
                                  <div className="text-xs text-gray-400 py-1">{t('layout:explorer.noNodes')}</div>
                                ) : (
                                  workflowNodesList.map(node => {
                                    const { icon: IconComponent, color } = getNodeIcon(node.type || '');
                                    const isSelected = selectedNodeId === node.id;
                                    return (
                                      <div
                                        key={node.id}
                                        className={classNames(
                                          'relative group flex items-center px-2 py-1 rounded cursor-pointer workflow-explorer-item',
                                          isSelected ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-100 text-gray-700'
                                        )}
                                        onClick={() => handleNodeClick(node.id)}
                                      >
                                        <span className={`mr-1.5 ${color}`}>
                                          {React.createElement(IconComponent as React.ComponentType<IconBaseProps>, { size: 12 })}
                                        </span>
                                        <span className="truncate text-sm font-medium flex-1">{getNodeDisplayName(node)}</span>
                                        <button
                                          onClick={(e) => handleRemoveNode(node, e)}
                                          className="absolute right-1 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center bg-white border border-gray-200 rounded shadow-sm z-10 p-1 hover:bg-red-100"
                                          title={t('layout:explorer.deleteNode')}
                                        >
                                          <IconWrapper icon={FiTrash2} size={12} />
                                        </button>
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
  };

  /** 폴더 노드 — 하위 폴더를 먼저, 그 다음 이 폴더의 워크플로를 그린다 */
  const renderFolder = (node: FolderNode): React.ReactNode => (
    <>
      {node.children.map((child) => {
        const open = !collapsedFolders[child.path];
        return (
          <div key={'f:' + child.path}>
            <div
              {...dropProps(child.path)}
              className={classNames('relative flex items-center group cursor-pointer px-2 py-1 rounded hover:bg-gray-100 text-gray-700 workflow-explorer-item', dropHighlight(child.path))}
              onClick={() => toggleFolder(child.path)}
              title={child.path}
            >
              <span className={`tree-chevron ${open ? 'open' : ''}`}>
                <IconWrapper icon={FiChevronRight} size={14} />
              </span>
              <span className="ml-1 text-amber-500"><IconWrapper icon={FiFolder} size={13} /></span>
              <span className="ml-1 flex-1 truncate font-medium">{child.name}</span>
              <span className="text-[0.625rem] text-gray-400 mr-1">{child.total}</span>
              {!readOnly && <span className="absolute right-1 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center bg-white border border-gray-200 rounded shadow-sm z-10 gap-0.5">
                <button onClick={(e) => { e.stopPropagation(); handleAddWorkflow(selectedProject!.id, child.path); }}
                  className="p-1 rounded hover:bg-gray-200" title={t('layout:explorer.addToFolder')}>
                  <IconWrapper icon={FiPlus} size={12} />
                </button>
                <button onClick={(e) => { e.stopPropagation(); setWizardFolder(child.path); setWizardOpen(true); }}
                  className="p-1 rounded hover:bg-gray-200" title={t('layout:explorer.wizardInFolder')}>
                  <IconWrapper icon={FiZap} size={12} />
                </button>
                <button onClick={(e) => { e.stopPropagation(); handleAddFolder(child.path); }}
                  className="p-1 rounded hover:bg-gray-200" title={t('layout:explorer.addSubfolder')}>
                  <IconWrapper icon={FiFolderPlus} size={12} />
                </button>
                <button onClick={(e) => { e.stopPropagation(); handleRenameFolder(child); }}
                  className="p-1 rounded hover:bg-gray-200" title={t('layout:explorer.renameFolderTitle')}>
                  <IconWrapper icon={FiEdit2} size={12} />
                </button>
                <button onClick={(e) => { e.stopPropagation(); handleDeleteFolder(child); }}
                  className="p-1 rounded hover:bg-red-100" title={t('layout:explorer.deleteFolderHint')}>
                  <IconWrapper icon={FiTrash2} size={12} />
                </button>
              </span>}
            </div>
            {open && (
              <div className="ml-4 border-l border-gray-100 pl-1">
                {child.total === 0 && child.children.length === 0 && (
                  <div className="text-[0.6875rem] text-gray-400 py-0.5 px-2">{t('layout:explorer.emptyFolder')}</div>
                )}
                {renderFolder(child)}
              </div>
            )}
          </div>
        );
      })}
      {node.workflows.map((w) => renderWorkflow(w))}
    </>
  );

  return (
    <>
      <aside
        data-point="panel:explorer"
        className={classNames(
          'h-full flex flex-col w-80 shrink-0',
          'bg-white border-r border-gray-200'
        )}
      >
        {/* 제목은 고정하고 목록만 스크롤한다 — 길게 내려가면 지금 무엇을 보는 중인지 사라졌다 */}
        <div className="shrink-0 pt-4 px-4 pb-2 text-xs text-gray-500 font-semibold tracking-wide uppercase bg-white">
          {t('layout:explorer.heading')}
        </div>
        <div className="flex-1 min-h-0 overflow-auto px-2 pb-4">
          <div>
            {selectedProject && (
              <div key={selectedProject.id}>
                <div {...dropProps('')} className={classNames('flex items-center group cursor-pointer px-2 py-1 rounded hover:bg-gray-100', dropHighlight(''))}
                  onClick={() => toggleProject(selectedProject.id)} title={readOnly ? undefined : t('layout:explorer.dropToRoot')}>
                  <IconWrapper icon={expandedProjects[selectedProject.id] ? FiChevronDown : FiChevronRight} size={16} />
                  <span className="ml-1 font-bold text-gray-800 flex-1 truncate">{selectedProject.name}</span>
                  {selectedProject.relation && selectedProject.relation !== 'owner' && (
                    <span className="ml-1 px-1.5 text-[0.625rem] leading-4 rounded bg-gray-100 text-gray-500 shrink-0"
                      title={t('layout:members.myRoleTitle')}>{t(`layout:members.role.${selectedProject.relation}`)}</span>
                  )}
                  <span className="flex gap-1 ml-2">
                    {!readOnly && (<>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleAddWorkflow(selectedProject.id); }}
                      className="p-1 rounded hover:bg-gray-200"
                      title={t('layout:explorer.addWorkflow')}
                    >
                      <IconWrapper icon={FiPlus} size={14} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setWizardFolder(''); setWizardOpen(true); }}
                      className="p-1 rounded hover:bg-gray-200"
                      title={t('layout:explorer.wizard')}
                    >
                      <IconWrapper icon={FiZap} size={14} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleAddFolder(''); }}
                      className="p-1 rounded hover:bg-gray-200"
                      title={t('layout:explorer.addFolderHint')}
                    >
                      <IconWrapper icon={FiFolderPlus} size={14} />
                    </button>
                    </>)}
                    <button
                      onClick={(e) => { e.stopPropagation(); setProjectSettingsOpen(true); }}
                      className="p-1 rounded hover:bg-gray-200"
                      title={t('layout:projectSettings.title')}
                    >
                      <IconWrapper icon={FiSettings} size={14} />
                    </button>
                  </span>
                </div>
                {projectSettingsOpen && (
                  <ProjectSettingsDialog projectId={selectedProject.id} onClose={() => setProjectSettingsOpen(false)} />
                )}
                {expandedProjects[selectedProject.id] && (
                  <div className="ml-5 border-l border-gray-200 pl-2">
                    {tree && tree.total === 0 && tree.children.length === 0 ? (
                      <WorkflowLoadNotice compact emptyHint={<div className="text-xs text-gray-400 py-1">{t('layout:explorer.noWorkflows')}</div>} />
                    ) : (
                      tree && renderFolder(tree)
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* 워크플로 설정 모달 */}
      {wizardOpen && selectedProject && <WorkflowWizard projectId={selectedProject.id} folder={wizardFolder} onClose={() => setWizardOpen(false)} />}
      {settingsModalOpen && currentEditingWorkflow && (
        <WorkflowSettingsDialog
          onClose={() => {
            setSettingsModalOpen(false);
            setCurrentEditingWorkflow(null);
          }}
          workflow={currentEditingWorkflow}
          isNew={isNewWorkflow}
          folders={folderPaths}
          onSave={handleSaveWorkflow}
        />
      )}
    </>
  );
};

export default WorkflowExplorer;